import type { WorkspaceFlavour } from '@pique/env/workspace';
import type { WorkspaceMetadata, WorkspacesService } from '@toeverything/infra';
import { Service } from '@toeverything/infra';

export class ImportTemplateService extends Service {
  constructor(private readonly workspacesService: WorkspacesService) {
    super();
  }

  async importToWorkspace(
    workspaceMetadata: WorkspaceMetadata,
    docBinary: Uint8Array
  ) {
    const { workspace, dispose: disposeWorkspace } =
      this.workspacesService.openAndActivate({
        metadata: workspaceMetadata,
      });
    try {
      await workspace.engine.waitForRootDocReady();
      await workspace.engine.waitForDocSynced();
      const newDoc = workspace.docCollection.createDoc({});
      if (!newDoc) {
        throw new Error('[ImportTemplate] Failed to create doc');
      }
      await workspace.engine.doc.storage.behavior.doc.set(
        newDoc.spaceDoc.guid,
        docBinary
      );
      return newDoc.id;
    } finally {
      disposeWorkspace();
    }
  }

  async importToNewWorkspace(
    flavour: WorkspaceFlavour,
    workspaceName: string,
    docBinary: Uint8Array
  ) {
    const meta = await this.workspacesService.create(
      flavour,
      async docCollection => {
        docCollection.meta.initialize();
        docCollection.meta.setName(workspaceName);
      }
    );

    const { workspace, dispose } = this.workspacesService.openAndActivate({
      metadata: meta,
    });
    try {
      await workspace.engine.waitForRootDocReady();
      await workspace.engine.waitForDocSynced();
      const newDoc = workspace.docCollection.createDoc({});
      if (!newDoc) {
        throw new Error('[ImportTemplate] Failed to create doc');
      }
      await workspace.engine.doc.storage.behavior.doc.set(
        newDoc.spaceDoc.guid,
        docBinary
      );
      return { workspaceId: meta.id, docId: newDoc.id };
    } finally {
      dispose();
    }
  }
}
